import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import db, { initDB, formatChapterId, ProgressStatus } from '../db';
import { AudioContext } from '../contexts/AudioContext';

function BooksGrid({ closeMenu, setSelectedBook, selectedBook, onBackToBooks }) {
  const [mode, setMode] = useState('listening');
  const [books, setBooks] = useState([]);
  const { chapterProgress, setChapterProgress, setCurrentChapter, setIsPlaying, updateSession } =
    useContext(AudioContext);

  useEffect(() => {
    const fetchData = async () => {
      await initDB();
      const bookList = await db.books.toArray();
      setBooks(bookList);
    };
    fetchData();
  }, []);

  const getBookStatusClass = (book) => {
    const bookChapters = Array.from(
      { length: book.chapters }, 
      (_, i) => formatChapterId(book.id, i + 1)
    );
    
    const completedCount = bookChapters.filter(
      ch => chapterProgress[ch]?.status === ProgressStatus.COMPLETED
    ).length;

    const inProgressCount = bookChapters.filter(
      ch => chapterProgress[ch]?.status === ProgressStatus.IN_PROGRESS
    ).length;

    if (completedCount === book.chapters) return 'completed';
    if (completedCount > 0 || inProgressCount > 0) return 'in-progress';
    return 'not-started';
  };

  const getChapterStatusClass = (chapterId) => {
    const progress = chapterProgress[chapterId];
    if (!progress) return 'not-started';
    
    switch (progress.status) {
      case ProgressStatus.COMPLETED:
        return 'completed';
      case ProgressStatus.IN_PROGRESS:
        return 'in-progress';
      default:
        return 'not-started';
    }
  };

  const toggleMode = () => setMode(mode === 'listening' ? 'tracking' : 'listening');

  const handleBookClick = (book) => {
    setSelectedBook(book);
  };

  const handleChapterClick = async (chapter) => {
    if (mode === 'listening') {
      const chapterData = {
        bookId: selectedBook.id,
        book: selectedBook.name,
        chapter,
        url: getAudioUrl(selectedBook.id, selectedBook.name, chapter),
      };
      setCurrentChapter(chapterData);
      setIsPlaying(true);
      updateSession(chapterData);
      closeMenu(true);
    } else if (mode === 'tracking') {
      const chapterId = formatChapterId(selectedBook.id, chapter);
      toggleChapter(chapterId);
    }
  };

  const toggleChapter = async (chapterId) => {
    const progress = chapterProgress[chapterId];
    const newStatus = (!progress || progress.status !== ProgressStatus.COMPLETED) 
      ? ProgressStatus.COMPLETED 
      : ProgressStatus.NOT_STARTED;

    const newProgress = {
      chapter_id: chapterId,
      status: newStatus,
      progress_percent: newStatus === ProgressStatus.COMPLETED ? 100 : 0,
      last_updated_at: new Date().toISOString(),
      dirty: 1
    };

    await db.chapterProgress.put(newProgress);
    setChapterProgress(prev => ({
      ...prev,
      [chapterId]: { status: newStatus, progress_percent: newProgress.progress_percent }
    }));
  };

  const tickAll = async () => {
    const allChapters = Array.from(
      { length: selectedBook.chapters }, 
      (_, i) => formatChapterId(selectedBook.id, i + 1)
    );
    
    const updates = allChapters.map(chapterId => ({
      chapter_id: chapterId,
      status: ProgressStatus.COMPLETED,
      progress_percent: 100,
      last_updated_at: new Date().toISOString(),
      dirty: 1
    }));

    await db.chapterProgress.bulkPut(updates);
    
    const newProgress = Object.fromEntries(
      allChapters.map(ch => [ch, { status: ProgressStatus.COMPLETED, progress_percent: 100 }])
    );
    setChapterProgress(prev => ({ ...prev, ...newProgress }));
  };

  const untickAll = async () => {
    const allChapters = Array.from(
      { length: selectedBook.chapters }, 
      (_, i) => formatChapterId(selectedBook.id, i + 1)
    );
    
    const updates = allChapters.map(chapterId => ({
      chapter_id: chapterId,
      status: ProgressStatus.NOT_STARTED,
      progress_percent: 0,
      last_updated_at: new Date().toISOString(),
      dirty: 1
    }));

    await db.chapterProgress.bulkPut(updates);
    
    const newProgress = Object.fromEntries(
      allChapters.map(ch => [ch, { status: ProgressStatus.NOT_STARTED, progress_percent: 0 }])
    );
    setChapterProgress(prev => ({ ...prev, ...newProgress }));
  };

  const allCompleted = selectedBook && Array.from(
    { length: selectedBook.chapters }, 
    (_, i) => formatChapterId(selectedBook.id, i + 1)
  ).every(ch => chapterProgress[ch]?.status === ProgressStatus.COMPLETED);

  const anyCompleted = selectedBook && Array.from(
    { length: selectedBook.chapters }, 
    (_, i) => formatChapterId(selectedBook.id, i + 1)
  ).some(ch => chapterProgress[ch]?.status === ProgressStatus.COMPLETED);

  const getAudioUrl = (bookId, bookName, chapter) => {
    const paddedBookId = bookId.toString().padStart(2, '0');
    const paddedChapter = chapter.toString().padStart(3, '0');
    const unpaddedChapter = chapter.toString();
    return `/static/audio/esv/${paddedBookId}${paddedChapter}_${bookName} ${unpaddedChapter}.mp3`;
  };

  const getBookName = (book) => book.abbreviation || book.name;

  const getChapterProgressIndicator = (chapterId) => {
    const progress = chapterProgress[chapterId];
    if (!progress || progress.status === ProgressStatus.NOT_STARTED) return '';
    if (progress.status === ProgressStatus.COMPLETED) return '✓';
    return `${Math.round(progress.progress_percent)}%`;
  };

  return (
    <div className="books-grid">
      <div className="grid-header">
        <div className="grid-header-left">
          {selectedBook && (
            <button className="back-button" onClick={() => onBackToBooks ? onBackToBooks() : setSelectedBook(null)}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Books
            </button>
          )}
        </div>
        <div className="grid-header-right">
          <label className="switch">
            <input
              type="checkbox"
              checked={mode === 'tracking'}
              onChange={toggleMode}
            />
            <span className="switch-label">Edit Progress</span>
            <span className="slider round"></span>
          </label>
        </div>
      </div>
      {selectedBook && (
        <div className="chapter-header">
          <h2>{selectedBook.name}</h2>
          {mode === 'tracking' && (
            <button 
              onClick={allCompleted ? untickAll : tickAll}
              className="select-all-button"
            >
              {allCompleted ? 'Deselect All' : (anyCompleted ? 'Select All' : 'Select All')}
            </button>
          )}
        </div>
      )}
      {selectedBook ? (
        <div className="grid">
          {Array.from({ length: selectedBook.chapters }, (_, i) => i + 1).map((chapter) => {
            const chapterId = formatChapterId(selectedBook.id, chapter);
            return (
              <div
                key={chapter}
                className={`grid-item ${getChapterStatusClass(chapterId)}`}
                onClick={() => handleChapterClick(chapter)}
              >
                <div>Chapter {chapter}</div>
                <div className="progress-indicator">
                  {getChapterProgressIndicator(chapterId)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid">
          {books.map((book) => (
            <div
              key={book.id}
              className={`grid-item ${getBookStatusClass(book)}`}
              onClick={() => handleBookClick(book)}
            >
              {getBookName(book)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BooksGrid;